/** Which of the server's issues (or the hub's hints) belong to one field. Pure. */
import type { SiteSeoIssue, SiteSeoMatch } from './seo-types';

type Located = Pick<SiteSeoIssue, 'path' | 'field' | 'language'>;

/**
 * The issues for `field` at `path` (null = site level: the issues without a path). With a `language`, an issue for
 * another language is left out, and one for no language (field-wide, e.g. the canonical) is kept; without one, every
 * language matches. Works on the hub's hints too (same location keys).
 */
export const issuesFor = <T extends Located = SiteSeoIssue>(
  issues: readonly T[],
  path: string | null,
  field: string,
  language?: string,
): T[] =>
  issues.filter(
    (issue) =>
      (path === null ? issue.path === undefined || issue.path === '' : issue.path === path) &&
      issue.field === field &&
      (language === undefined || issue.language === undefined || issue.language === language),
  );

/** The first `error`-level message, shown as is under its field. */
export const firstErrorMessage = (issues: readonly SiteSeoIssue[]): string | undefined =>
  issues.find((issue) => issue.level === 'error' && issue.message !== '')?.message;

/** The first `warning`-level message, shown as is under its field while it has no error. */
export const firstWarningMessage = (issues: readonly SiteSeoIssue[]): string | undefined =>
  issues.find((issue) => issue.level === 'warning' && issue.message !== '')?.message;

/** The issues for a language the site does not have: no language tab shows them, so the page shows them once. */
export const orphanLanguageIssues = (issues: readonly SiteSeoIssue[], languages: readonly string[]): SiteSeoIssue[] =>
  issues.filter(
    (issue) => issue.language !== undefined && issue.language !== '' && !languages.includes(issue.language),
  );

/** The open view: the route (null = Site defaults), its match, and the language on screen. */
export interface IssueView {
  path: string | null;
  language: string;
  match?: SiteSeoMatch;
}

/** The fields a panel of Site defaults renders an issue under. */
const SITE_FIELDS: readonly string[] = [
  'title_template',
  'description',
  'footer_text',
  'share_image',
  'llms_summary',
  'llms_facts',
];

/** The fields a panel of a route renders an issue under (the image and canonical only where their field shows). */
const routeFields = (match: SiteSeoMatch | undefined): readonly string[] => [
  'path',
  'title',
  'description',
  'footer_text',
  'noindex',
  ...(match !== 'wildcard' ? ['share_image'] : []),
  ...(match === 'exact' ? ['canonical'] : []),
];

const siteLevel = (issue: Located): boolean => issue.path === undefined || issue.path === '';

/** Whether a panel of `view` shows `issue` under its field (mirrors each panel's `issuesFor` call). */
const shownIn = (issue: SiteSeoIssue, view: IssueView): boolean => {
  if (issue.language !== undefined && issue.language !== '' && issue.language !== view.language) return false;
  if (issue.field === undefined || issue.field === '') return false;
  if (view.path === null) return siteLevel(issue) && SITE_FIELDS.includes(issue.field);
  return issue.path === view.path && routeFields(view.match).includes(issue.field);
};

/**
 * The errors (they block Publish) that no panel of the open view shows: for another language, another route, or no
 * field at all. Issues for a language the site does not have are left out (the page lists them on their own).
 */
export const hiddenBlockingIssues = (
  issues: readonly SiteSeoIssue[],
  view: IssueView,
  languages: readonly string[],
): SiteSeoIssue[] => {
  const orphans = new Set(orphanLanguageIssues(issues, languages));
  return issues.filter((issue) => issue.level === 'error' && !orphans.has(issue) && !shownIn(issue, view));
};

/** The routes holding an error, `*` for the site level (the route list marks them). */
export const pathsWithErrors = (issues: readonly SiteSeoIssue[]): Set<string> =>
  new Set(
    issues.filter((issue) => issue.level === 'error').map((issue) => (siteLevel(issue) ? '*' : issue.path ?? '*')),
  );
